import { createSelector, createSlice } from '@reduxjs/toolkit';
import { logoutUser } from '@/features/auth/authThunks';
import type { RootState } from '@/store/root-state';
import type { RecoveryCaseSummary, RecoveryExecutionFeedback, RecoveryState } from './recoveryTypes';
import {
  approveRecoveryCase,
  cancelRecoveryCase,
  executeRecoveryCase,
  fetchRecoveryCase,
  fetchRecoveryCases,
  rejectRecoveryCase,
  revokeRecoveryCaseApproval,
} from './recoveryThunks';

const initialState: RecoveryState = {
  cases: [],
  isLoadingCases: false,
  casesError: null,
  hasFetchedCases: false,
  nextToken: null,
  selectedCase: null,
  selectedEvidence: null,
  isLoadingCase: false,
  caseError: null,
  isSubmittingDecision: false,
  isRevokingApproval: false,
  isCancelling: false,
  isExecuting: false,
  executionFeedback: null,
};

function mergeSummary(detail: RecoveryCaseSummary | null, summary: RecoveryCaseSummary): RecoveryCaseSummary | null {
  return detail?.caseId === summary.caseId ? { ...detail, ...summary } : detail;
}

const recoverySlice = createSlice({
  name: 'recovery',
  initialState,
  reducers: {
    clearSelectedRecoveryCase: (state) => {
      state.selectedCase = null;
      state.selectedEvidence = null;
      state.caseError = null;
      state.executionFeedback = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecoveryCases.pending, (state, action) => {
        state.isLoadingCases = true;
        if (!action.meta.arg?.nextToken) {
          state.hasFetchedCases = false;
          state.cases = [];
          state.nextToken = null;
        }
        state.casesError = null;
      })
      .addCase(fetchRecoveryCases.fulfilled, (state, action) => {
        state.isLoadingCases = false;
        state.hasFetchedCases = true;
        state.cases = action.meta.arg?.nextToken
          ? [
              ...state.cases,
              ...action.payload.cases.filter(
                (candidate) => !state.cases.some((existing) => existing.caseId === candidate.caseId),
              ),
            ]
          : action.payload.cases;
        state.nextToken = action.payload.nextToken;
      })
      .addCase(fetchRecoveryCases.rejected, (state, action) => {
        state.isLoadingCases = false;
        if (action.meta.aborted) return;
        state.hasFetchedCases = true;
        if (!action.meta.arg?.nextToken) state.cases = [];
        state.casesError = action.payload?.message || action.error.message || 'Failed to load recovery cases';
      })
      .addCase(fetchRecoveryCase.pending, (state) => {
        state.isLoadingCase = true;
        state.caseError = null;
      })
      .addCase(fetchRecoveryCase.fulfilled, (state, action) => {
        state.isLoadingCase = false;
        state.selectedCase = action.payload.summary;
        state.selectedEvidence = action.payload.evidence;
        state.caseError = null;
      })
      .addCase(fetchRecoveryCase.rejected, (state, action) => {
        state.isLoadingCase = false;
        if (action.meta.aborted) return;
        state.caseError = action.payload?.message || action.error.message || 'Failed to load recovery case';
      })
      .addCase(approveRecoveryCase.pending, (state) => {
        state.isSubmittingDecision = true;
        state.caseError = null;
      })
      .addCase(rejectRecoveryCase.pending, (state) => {
        state.isSubmittingDecision = true;
        state.caseError = null;
      })
      .addCase(approveRecoveryCase.fulfilled, (state, action) => {
        state.isSubmittingDecision = false;
        state.selectedCase = mergeSummary(state.selectedCase, action.payload);
      })
      .addCase(rejectRecoveryCase.fulfilled, (state, action) => {
        state.isSubmittingDecision = false;
        state.selectedCase = mergeSummary(state.selectedCase, action.payload);
      })
      .addCase(approveRecoveryCase.rejected, (state, action) => {
        state.isSubmittingDecision = false;
        state.caseError = action.payload?.message || action.error.message || 'Failed to update recovery case';
      })
      .addCase(rejectRecoveryCase.rejected, (state, action) => {
        state.isSubmittingDecision = false;
        state.caseError = action.payload?.message || action.error.message || 'Failed to update recovery case';
      })
      .addCase(revokeRecoveryCaseApproval.pending, (state) => {
        state.isRevokingApproval = true;
        state.caseError = null;
      })
      .addCase(revokeRecoveryCaseApproval.fulfilled, (state, action) => {
        state.isRevokingApproval = false;
        state.selectedCase = mergeSummary(state.selectedCase, action.payload);
      })
      .addCase(revokeRecoveryCaseApproval.rejected, (state, action) => {
        state.isRevokingApproval = false;
        state.caseError = action.payload?.message || action.error.message || 'Failed to revoke recovery approval';
      })
      .addCase(cancelRecoveryCase.pending, (state) => {
        state.isCancelling = true;
        state.caseError = null;
      })
      .addCase(cancelRecoveryCase.fulfilled, (state, action) => {
        state.isCancelling = false;
        state.selectedCase = mergeSummary(state.selectedCase, action.payload);
      })
      .addCase(cancelRecoveryCase.rejected, (state, action) => {
        state.isCancelling = false;
        state.caseError = action.payload?.message || action.error.message || 'Failed to cancel recovery';
      })
      .addCase(executeRecoveryCase.pending, (state) => {
        state.isExecuting = true;
        state.caseError = null;
        state.executionFeedback = null;
      })
      .addCase(executeRecoveryCase.fulfilled, (state, action) => {
        state.isExecuting = false;
        state.selectedCase = mergeSummary(state.selectedCase, action.payload);
        state.executionFeedback = null;
      })
      .addCase(executeRecoveryCase.rejected, (state, action) => {
        state.isExecuting = false;
        const feedback = action.payload?.details as RecoveryExecutionFeedback | undefined;
        state.executionFeedback = feedback ?? null;
        if (
          feedback &&
          (feedback.code === 'RECOVERY_EXECUTION_RETRY_AVAILABLE' ||
            feedback.code === 'RECOVERY_EXECUTION_REQUIRES_OPERATOR_REVIEW' ||
            feedback.code === 'RECOVERY_AUTH_SERVICE_UNAVAILABLE' ||
            feedback.code === 'RECOVERY_CASE_CONFLICT')
        ) {
          state.caseError = null;
          if (state.selectedCase && feedback.status && feedback.version !== undefined) {
            state.selectedCase = {
              ...state.selectedCase,
              status: feedback.status,
              version: feedback.version,
              failureCode: feedback.failureCode ?? state.selectedCase.failureCode,
              canRetryCompletion: feedback.canRetryCompletion ?? state.selectedCase.canRetryCompletion,
            };
          }
        } else {
          state.caseError = action.payload?.message || action.error.message || 'Failed to execute recovery';
        }
      })
      .addCase(logoutUser.fulfilled, () => ({ ...initialState }));
  },
});

export const { clearSelectedRecoveryCase } = recoverySlice.actions;
export default recoverySlice.reducer;

const selectRecoveryState = (state: RootState) => state.recovery;
export const selectRecoveryCases = createSelector(selectRecoveryState, (state) => state.cases);
export const selectRecoveryCasesLoading = createSelector(selectRecoveryState, (state) => state.isLoadingCases);
export const selectRecoveryCasesError = createSelector(selectRecoveryState, (state) => state.casesError);
export const selectRecoveryCasesFetched = createSelector(selectRecoveryState, (state) => state.hasFetchedCases);
export const selectRecoveryCasesNextToken = createSelector(selectRecoveryState, (state) => state.nextToken);
export const selectSelectedRecoveryCase = createSelector(selectRecoveryState, (state) => state.selectedCase);
export const selectSelectedRecoveryEvidence = createSelector(selectRecoveryState, (state) => state.selectedEvidence);
export const selectRecoveryCaseLoading = createSelector(selectRecoveryState, (state) => state.isLoadingCase);
export const selectRecoveryCaseError = createSelector(selectRecoveryState, (state) => state.caseError);
export const selectRecoveryDecisionSubmitting = createSelector(
  selectRecoveryState,
  (state) => state.isSubmittingDecision,
);
export const selectRecoveryApprovalRevoking = createSelector(selectRecoveryState, (state) => state.isRevokingApproval);
export const selectRecoveryCancelling = createSelector(selectRecoveryState, (state) => state.isCancelling);
export const selectRecoveryExecuting = createSelector(selectRecoveryState, (state) => state.isExecuting);
export const selectRecoveryExecutionFeedback = createSelector(selectRecoveryState, (state) => state.executionFeedback);
